/* Grubwick admin — shell: sidebar nav, route-based views, backend-loaded data. */
import React, { useEffect, useMemo, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  Link,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import "./admin.css";
import {
  blankRestaurant,
  type Restaurant,
  type MenuItemRecord,
} from "../../data/adminData";
import { fetchRestaurants, fetchLocationMenu } from "../../api/restaurants";
import { ToastProvider } from "./ui/ui";
import { ListView } from "./ListView/ListView";
import { RestaurantEditor } from "./RestaurantEditor/RestaurantEditor";
import { ImportView } from "./ImportView/ImportView";
import { Logo, Store, Plus, Camera, Chart, Tags, Menu, Spinner } from "./icons/icons";
import { AccountControl } from "../Auth/AccountControl";
import { useAuth } from "../Auth/AuthProvider";

type NavId = "list" | "add" | "import" | "analytics" | "tags";

/* Map the current path to the highlighted sidebar item. Editing an existing
   restaurant keeps "Restaurants" active; the add form keeps "Add restaurant". */
function activeNavFor(pathname: string): NavId {
  if (pathname.startsWith("/admin/add")) return "add";
  if (pathname.startsWith("/admin/import")) return "import";
  return "list";
}

/* ── sidebar ── */
function Sidebar({
  open,
  onClose,
  canCreate,
}: {
  open: boolean;
  onClose: () => void;
  /* Add-restaurant and import are creation flows — admins only. */
  canCreate: boolean;
}): React.ReactElement {
  const navigate = useNavigate();
  const active = activeNavFor(useLocation().pathname);

  const go = (id: NavId) => {
    if (id === "add") navigate("/admin/add");
    else if (id === "import") navigate("/admin/import");
    else if (id === "list") navigate("/admin");
    onClose();
  };

  const item = (
    id: NavId,
    label: string,
    Icon: (p: { size?: number }) => React.ReactElement,
    opts: { disabled?: boolean; soon?: boolean } = {},
  ) => (
    <button
      className={"nav-btn" + (active === id ? " active" : "")}
      disabled={opts.disabled}
      onClick={() => {
        if (opts.disabled) return;
        go(id);
      }}
    >
      <Icon size={18} /> {label}
      {opts.soon && <span className="soon">Soon</span>}
    </button>
  );
  return (
    <aside className={"side" + (open ? " open" : "")}>
      <div className="side-brand">
        <Logo size={40} />
        <div>
          <div className="nm">Grubwick</div>
          <div className="tag">Admin</div>
        </div>
      </div>

      <div className="nav-grp">Manage</div>
      {item("list", "Restaurants", Store)}
      {canCreate && item("add", "Add restaurant", Plus)}
      {canCreate && item("import", "Import from photo", Camera)}

      <div className="nav-grp">Insights</div>
      {item("analytics", "Analytics", Chart, { disabled: true, soon: true })}
      {item("tags", "Categories", Tags, { disabled: true, soon: true })}

      <div className="side-spacer"></div>

      <div className="side-foot">
        <AccountControl variant="footer" />
      </div>
    </aside>
  );
}

/* ── route views ──
   Each wrapper binds the shared data store to a view component, translating the
   view's callbacks into navigation. The views themselves are unchanged. */

/* Menu items handed from the import flow to the editor via router state, so the
   work done extracting a photo isn't lost on the way to "Review in editor". */
interface ImportNavState { extractedItems?: MenuItemRecord[]; }
function readExtracted(state: unknown): MenuItemRecord[] {
  return (state as ImportNavState | null)?.extractedItems ?? [];
}

/* The whole window scrolls in the admin shell (the sidebar is sticky and there
   is no inner scroll container), so React Router leaves the scroll position
   untouched across navigations — opening a restaurant, scrolling down, then
   going back would leave the list scrolled down too. Reset to the top whenever
   the path changes. */
function ScrollToTop(): null {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/* A simple status panel reusing the existing page layout, shown while the
   restaurant list is loading or after a fetch error. */
function StatusPage({ message }: { message: string }): React.ReactElement {
  return (
    <div className="page">
      <p className="sub">{message}</p>
    </div>
  );
}

function ListRoute({
  data,
  loading,
  error,
}: {
  data: Restaurant[];
  loading: boolean;
  error: string | null;
}): React.ReactElement {
  const navigate = useNavigate();
  const { isAdmin, canEditRestaurant } = useAuth();
  if (loading) return <StatusPage message="Loading restaurants…" />;
  if (error) return <StatusPage message={error} />;
  return (
    <ListView
      restaurants={data}
      onOpen={(id) => navigate("/admin/restaurant/" + id)}
      onAdd={() => navigate("/admin/add")}
      canAdd={isAdmin}
      canEdit={canEditRestaurant}
    />
  );
}

function ImportRoute({ data }: { data: Restaurant[] }): React.ReactElement {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  // Import is a create/bulk-entry flow — admins only. Owners edit existing spots.
  if (!isAdmin) return <Navigate to="/admin" replace />;
  return (
    <ImportView
      restaurants={data}
      onGoEditor={(t, items) =>
        navigate(t === "new" ? "/admin/add" : "/admin/restaurant/" + t, {
          state: { extractedItems: items } satisfies ImportNavState,
        })
      }
    />
  );
}

function AddRoute({
  onSave,
}: {
  onSave: (r: Restaurant) => void;
}): React.ReactElement {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { state } = useLocation();
  // Creating a restaurant is admin-only. Guard the route directly so a typed
  // URL can't reach the create form.
  const [draft] = useState(() => ({
    ...blankRestaurant(),
    menu: readExtracted(state),
  }));
  if (!isAdmin) return <Navigate to="/admin" replace />;
  return (
    <RestaurantEditor
      key={draft.id}
      source={draft}
      isNew
      onSave={(r) => {
        onSave(r);
        navigate("/admin/restaurant/" + r.id, { replace: true });
      }}
      onDelete={() => navigate("/admin")}
      onBack={() => navigate("/admin")}
    />
  );
}

/* Loads the restaurant's menu from the API, then renders the editor with a
   complete record. The editor snapshots `source` on mount, so we wait for the
   menu before mounting it. */
function EditorWithMenu({
  base,
  extra,
  readOnly,
  canDelete,
  onSave,
  onDelete,
  onBack,
}: {
  base: Restaurant;
  extra: MenuItemRecord[];
  readOnly: boolean;
  canDelete: boolean;
  onSave: (r: Restaurant) => void;
  onDelete: (id: string) => void;
  onBack: () => void;
}): React.ReactElement {
  const [menu, setMenu] = useState<MenuItemRecord[] | null>(null);
  // Which location's menu is shown. Restaurants currently have one location, but
  // the backend models several; the editor's switcher updates this.
  const [locationId, setLocationId] = useState<string>(
    () => base.locations[0]?.id ?? "",
  );
  // True while a location switch is fetching (the editor stays mounted; the menu
  // panel shows an inline indicator). Distinct from the first-load StatusPage.
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    let active = true;
    setSwitching(true);
    // A restaurant with no location has no menu to fetch — open with just the
    // imported items. Otherwise load the selected location's menu and append
    // anything imported from a photo after it.
    const load = locationId
      ? fetchLocationMenu(base.id, locationId).then((items) => [
          ...items,
          ...extra,
        ])
      : Promise.resolve([...extra]);
    load
      .then((items) => active && setMenu(items))
      .catch(() => active && setMenu([...extra])) // Open with just the imported items on failure.
      .finally(() => active && setSwitching(false));
    return () => {
      active = false;
    };
  }, [base.id, locationId, extra]);

  if (menu === null) return <StatusPage message="Loading menu…" />;

  return (
    <RestaurantEditor
      key={base.id}
      source={{ ...base, menu }}
      isNew={false}
      locations={base.locations}
      selectedLocationId={locationId}
      onSelectLocation={setLocationId}
      menuLoading={switching}
      readOnly={readOnly}
      canDelete={canDelete}
      onSave={onSave}
      onDelete={onDelete}
      onBack={onBack}
    />
  );
}

function EditRoute({
  data,
  loading,
  onSave,
  onDelete,
}: {
  data: Restaurant[];
  loading: boolean;
  onSave: (r: Restaurant) => void;
  onDelete: (id: string) => void;
}): React.ReactElement {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { isAdmin, canEditRestaurant } = useAuth();
  const extra = useMemo(() => readExtracted(state), [state]);
  if (loading) return <StatusPage message="Loading restaurant…" />;
  const base = data.find((x) => x.id === id);
  if (!base) return <Navigate to="/admin" replace />;
  // Owners may edit only the restaurants they own; everything else opens
  // read-only. Deleting is admin-only.
  return (
    <EditorWithMenu
      base={base}
      extra={extra}
      readOnly={!canEditRestaurant(base.id)}
      canDelete={isAdmin}
      onSave={onSave}
      onDelete={(rid) => {
        onDelete(rid);
        navigate("/admin");
      }}
      onBack={() => navigate("/admin")}
    />
  );
}

/* Shown while the caller's role is still resolving — avoids flashing the panel
   or an access-denied screen before GET /gw/me answers. */
function AccessPending(): React.ReactElement {
  return (
    <div className="page" style={{ display: "grid", placeItems: "center" }}>
      <p className="sub">
        <span className="spin" style={{ marginRight: 8 }}>
          <Spinner size={16} />
        </span>
        Checking access…
      </p>
    </div>
  );
}

/* Shown to callers who may not use the admin panel: regular users (signed in but
   without owner/admin) and signed-out visitors, who get a sign-in link. */
function AccessDenied({ signedIn }: { signedIn: boolean }): React.ReactElement {
  const location = useLocation();
  return (
    <div
      className="page"
      style={{ display: "grid", placeItems: "center", textAlign: "center" }}
    >
      <div>
        <p className="h-eyebrow">Admin</p>
        <h1>No access</h1>
        <p className="sub" style={{ maxWidth: 420, margin: "0 auto 20px" }}>
          {signedIn
            ? "Your account doesn’t have permission to manage restaurants. Contact an admin if you think this is a mistake."
            : "Please sign in with an authorized account to manage restaurants."}
        </p>
        <div
          style={{ display: "flex", gap: 10, justifyContent: "center" }}
        >
          {!signedIn && (
            <Link
              className="btn btn-primary"
              to="/login"
              state={{ from: location.pathname + location.search }}
            >
              Sign in
            </Link>
          )}
          <Link className="btn btn-ghost" to="/">
            Back to Grubwick
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ── app ── */
export function AdminApp(): React.ReactElement {
  const {
    loading: authLoading,
    roleLoading,
    canAccessAdmin,
    isAdmin,
    user,
  } = useAuth();
  const [data, setData] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drawer, setDrawer] = useState(false);

  // Apply the shipped appearance (light theme, compact density) on mount; clean up on unmount.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", "light");
    root.setAttribute("data-density", "compact");
    return () => {
      root.removeAttribute("data-theme");
      root.removeAttribute("data-density");
    };
  }, []);

  // Load the restaurant list from the backend once the caller is known to have
  // admin-panel access (skip the fetch entirely for denied/anonymous visitors).
  useEffect(() => {
    if (!canAccessAdmin) return;
    let active = true;
    setLoading(true);
    setError(null);
    fetchRestaurants()
      .then((rows) => active && setData(rows))
      .catch(
        (e: unknown) =>
          active &&
          setError(e instanceof Error ? e.message : "Failed to load restaurants"),
      )
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [canAccessAdmin]);

  function saveRestaurant(r: Restaurant) {
    setData((d) => {
      const exists = d.some((x) => x.id === r.id);
      return exists ? d.map((x) => (x.id === r.id ? r : x)) : [r, ...d];
    });
  }
  function deleteRestaurant(id: string) {
    setData((d) => d.filter((x) => x.id !== id));
  }

  // Gate the whole panel on role: wait while it resolves, then either deny
  // (users / anonymous) or render the shell (admins / owners).
  if (authLoading || roleLoading) {
    return (
      <div className="grub-admin">
        <ToastProvider>
          <AccessPending />
        </ToastProvider>
      </div>
    );
  }
  if (!canAccessAdmin) {
    return (
      <div className="grub-admin">
        <ToastProvider>
          <AccessDenied signedIn={!!user} />
        </ToastProvider>
      </div>
    );
  }

  return (
    <div className="grub-admin">
      <ToastProvider>
        <div className="shell">
          <Sidebar
            open={drawer}
            onClose={() => setDrawer(false)}
            canCreate={isAdmin}
          />
          <div
            className={"scrim" + (drawer ? " show" : "")}
            onClick={() => setDrawer(false)}
          ></div>
          <ScrollToTop />
          <div className="main">
            <div className="mtop">
              <button
                className="hamb"
                onClick={() => setDrawer(true)}
                aria-label="Menu"
              >
                <Menu size={20} />
              </button>
              <Logo size={30} />
              <span className="nm">Grubwick</span>
            </div>
            <Routes>
              <Route
                index
                element={
                  <ListRoute data={data} loading={loading} error={error} />
                }
              />
              <Route
                path="add"
                element={<AddRoute onSave={saveRestaurant} />}
              />
              <Route path="import" element={<ImportRoute data={data} />} />
              <Route
                path="restaurant/:id"
                element={
                  <EditRoute
                    data={data}
                    loading={loading}
                    onSave={saveRestaurant}
                    onDelete={deleteRestaurant}
                  />
                }
              />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
          </div>
        </div>
      </ToastProvider>
    </div>
  );
}

export default AdminApp;
